import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useResource, formatDate, titleCase } from "../lib/hooks";
import { EntityForm, Field } from "../components/EntityForm";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { ProviderOrgChart } from "../components/ProviderOrgChart";
import { NegotiatedRatesCard } from "../components/NegotiatedRatesCard";
import { fileViewUrl } from "../lib/files";
import type {
    Company, Handover, Medication, Participant, Referral, ServiceNote, Shift,
} from "../lib/types";
import type { CareCase } from "./Cases";
import type { PartnerRequest } from "./PartnerRequests";

type Tab = "overview" | "contacts" | "participants" | "roster" | "medications"
    | "notes" | "handovers" | "documents" | "risk" | "cases" | "requests"
    | "invoices" | "rates" | "settings";

type CompanyV2 = Company & {
    primary_contact?: string | null;
    website?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    notes?: string | null;
    postcode?: string | null;
    billing_email?: string | null;
    payment_terms_days?: number | null;
    agreement_start_date?: string | null;
    agreement_end_date?: string | null;
};

interface InvoiceRow {
    id: string; invoice_number: string; status: string;
    period_start: string; period_end: string; total: string; company_id: string;
}

interface DocRow {
    id: string; kind: string; category: string | null; note: string | null;
    file_id: string; file_name: string; company_id: string | null;
    participant_id: string | null;
    uploaded_by_name: string | null; created_at: string;
}

const companyFields = (): Field[] => [
    { name: "name", label: "Organisation name", kind: "text", required: true },
    { name: "kind", label: "Type", kind: "select", required: true, half: true, options: [
        { value: "provider", label: "Provider" },
        { value: "plan_manager", label: "Plan manager" },
        { value: "support_coordinator", label: "Support coordinator" },
        { value: "referrer", label: "Referrer" },
        { value: "supplier", label: "Supplier" },
        { value: "other", label: "Other" },
    ] },
    { name: "abn", label: "ABN", kind: "text", half: true,
      helpLink: { label: "Verify on ABN Lookup", href: "https://abr.business.gov.au/" } },
    { name: "ndis_registered", label: "NDIS registered provider", kind: "checkbox" },
    { name: "registration_id", label: "NDIS registration ID", kind: "text", half: true },
    { name: "primary_contact", label: "Primary contact", kind: "text", half: true },
    { name: "phone", label: "Phone", kind: "tel", half: true },
    { name: "email", label: "Email", kind: "email", half: true },
    { name: "billing_email", label: "Billing email", kind: "email", half: true,
      help: "Where this organisation's invoices are sent." },
    { name: "payment_terms_days", label: "Payment terms (days)", kind: "number", half: true, min: 0, max: 90 },
    { name: "agreement_start_date", label: "Agreement start", kind: "date", half: true,
      help: "The subcontractor agreement window with this provider." },
    { name: "agreement_end_date", label: "Agreement end", kind: "date", half: true },
    { name: "website", label: "Website", kind: "text", half: true },
    { name: "suburb", label: "Suburb", kind: "text", half: true },
    { name: "postcode", label: "Postcode", kind: "text", half: true },
    { name: "address", label: "Address", kind: "text" },
    { name: "notes", label: "Notes", kind: "textarea" },
];

/**
 * The provider profile — TrustCare's PRIMARY entity, given the same
 * full-record treatment as a participant (AJ, 4 Aug 2026): one page holding
 * everything about the relationship, each tab drawing from the modules that
 * already own the data, everything linked onward.
 */
export const ProviderProfile = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const companies = useResource<CompanyV2[]>("/companies");
    const contacts = useResource<Array<{ id: string; first_name: string; last_name: string; job_title: string | null; email: string | null; phone: string | null; company_id: string | null }>>("/contacts");
    const participants = useResource<Participant[]>("/participants");
    const cases = useResource<CareCase[]>("/cases");
    const requests = useResource<PartnerRequest[] & Array<{ company_id?: string }>>("/partner-requests");
    const invoices = useResource<InvoiceRow[]>("/invoices");
    const referrals = useResource<Referral[]>("/referrals");
    const docs = useResource<DocRow[]>("/partner-documents");
    const shifts = useResource<Shift[]>("/shifts");
    const medications = useResource<Medication[]>("/medications");
    const notes = useResource<ServiceNote[]>("/service-notes");
    const handovers = useResource<Handover[]>("/handovers");

    const [tab, setTab] = useState<Tab>("overview");
    const [editing, setEditing] = useState(false);

    const company = (companies.data ?? []).find((c) => c.id === id) ?? null;

    // Everything below is the provider's slice of the whole system: their
    // participants, and every clinical record that belongs to those people.
    const mine = useMemo(() => {
        const theirParticipants = (participants.data ?? []).filter((p) => p.company_id === id);
        const pids = new Set(theirParticipants.map((p) => p.id));
        const theirDocs = (docs.data ?? []).filter((d) =>
            d.company_id === id || (d.participant_id != null && pids.has(d.participant_id)));
        return {
            contacts: (contacts.data ?? []).filter((c) => c.company_id === id),
            participants: theirParticipants,
            cases: (cases.data ?? []).filter((c) => c.company_id === id),
            requests: ((requests.data ?? []) as Array<PartnerRequest & { company_id?: string }>)
                .filter((r) => r.company_id === id),
            invoices: (invoices.data ?? []).filter((i) => i.company_id === id),
            referrals: (referrals.data ?? []).filter((r) => r.company_id === id),
            docs: theirDocs.filter((d) => d.kind !== "risk_assessment"),
            riskDocs: theirDocs.filter((d) => d.kind === "risk_assessment"),
            roster: (shifts.data ?? [])
                .filter((s) => s.participant_id != null && pids.has(s.participant_id) && s.status !== "cancelled")
                .sort((a, b) => b.starts_at.localeCompare(a.starts_at)),
            medications: (medications.data ?? []).filter((m) => pids.has(m.participant_id)),
            notes: (notes.data ?? [])
                .filter((n) => pids.has(n.participant_id))
                .sort((a, b) => b.note_date.localeCompare(a.note_date)),
            handovers: (handovers.data ?? [])
                .filter((h) => pids.has(h.participant_id))
                .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)),
        };
    }, [contacts.data, participants.data, cases.data, requests.data, invoices.data,
        referrals.data, docs.data, shifts.data, medications.data, notes.data, handovers.data, id]);

    const participantName = useMemo(() => {
        const map = new Map(mine.participants.map((p) => [p.id, p.full_name]));
        return (pid: string | null) => (pid ? map.get(pid) ?? "—" : "—");
    }, [mine.participants]);

    if (companies.error) return <div className="error-banner">{companies.error}</div>;
    if (!company) {
        return companies.loading
            ? <div className="card"><div className="card-body empty">Loading…</div></div>
            : <div className="card"><div className="card-body empty">Organisation not found. <Link to="/providers">Back to organisations</Link></div></div>;
    }

    const openCases = mine.cases.filter((c) => c.status === "open").length;
    const pendingRequests = mine.requests.filter((r) => r.status === "new" || r.status === "in_review" || r.status === "info_requested").length;
    const unpaidInvoices = mine.invoices.filter((i) => i.status === "issued" || i.status === "sent").length;

    const TABS: Array<{ key: Tab; label: string }> = [
        { key: "overview", label: "Overview" },
        { key: "contacts", label: `Contacts (${mine.contacts.length})` },
        { key: "participants", label: `Participants (${mine.participants.length})` },
        { key: "roster", label: "Roster" },
        { key: "medications", label: `Medications (${mine.medications.filter((m) => m.status === "active").length})` },
        { key: "notes", label: `Service notes (${mine.notes.length})` },
        { key: "handovers", label: `Handovers (${mine.handovers.length})` },
        { key: "documents", label: `Documents (${mine.docs.length})` },
        { key: "risk", label: `Risk assessment (${mine.riskDocs.length})` },
        { key: "cases", label: `Cases (${mine.cases.length})` },
        { key: "requests", label: `Requests (${mine.requests.length})` },
        { key: "invoices", label: `Invoices (${mine.invoices.length})` },
        { key: "rates", label: "Rates" },
        { key: "settings", label: "Settings" },
    ];

    return (
        <div style={{ display: "grid", gap: "0.9rem" }}>
            {editing && (
                <EntityForm title={`Edit ${company.name}`} path="/companies"
                    fields={companyFields()} initial={company}
                    onClose={() => setEditing(false)}
                    onSaved={() => { setEditing(false); companies.reload(); }} />
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
                <button className="btn ghost small" onClick={() => navigate(-1)}>← Back</button>
                <div style={{ display: "flex", gap: "0.4rem" }}>
                    <button className="btn small" onClick={() => setEditing(true)}>Edit</button>
                </div>
            </div>

            <div className="card">
                <div className="card-body" style={{ display: "flex", alignItems: "center", gap: "0.9rem", flexWrap: "wrap" }}>
                    <InitialsAvatar name={company.name} size={44} />
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <h2 style={{ margin: 0 }}>{company.name}</h2>
                        <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: 2 }}>
                            {company.ref_code && <span className="mono" style={{ marginRight: 8 }}>{company.ref_code}</span>}
                            {titleCase(company.kind)} {company.abn ? `· ABN ${company.abn}` : ""} {company.suburb ? `· ${company.suburb}` : ""}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                        {company.ndis_registered
                            ? <span className="chip pass">NDIS registered{company.registration_id ? ` · ${company.registration_id}` : ""}</span>
                            : <span className="chip mute">Unregistered</span>}
                        <span className="chip info">{mine.participants.length} participants</span>
                        <span className={`chip ${openCases ? "pass" : "mute"}`}>{openCases} open cases</span>
                        <span className={`chip ${pendingRequests ? "warn" : "mute"}`}>{pendingRequests} pending requests</span>
                        <span className={`chip ${unpaidInvoices ? "warn" : "mute"}`}>{unpaidInvoices} unpaid invoices</span>
                    </div>
                </div>
            </div>

            <div className="tabs">
                {TABS.map((t) => (
                    <button key={t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <div className="grid cols-2">
                    <div className="card">
                        <div className="card-head"><h2>Details</h2></div>
                        <div className="card-body">
                            <dl className="kv">
                                <dt>Primary contact</dt><dd>{company.primary_contact ?? "—"}</dd>
                                <dt>Phone</dt><dd>{company.phone ?? "—"}</dd>
                                <dt>Email</dt><dd>{company.email ?? "—"}</dd>
                                <dt>Billing email</dt><dd>{company.billing_email ?? "—"}</dd>
                                <dt>Payment terms</dt><dd>{company.payment_terms_days != null ? `${company.payment_terms_days} days` : "—"}</dd>
                                <dt>Website</dt><dd>{company.website ?? "—"}</dd>
                                <dt>Address</dt><dd>{[company.address, company.suburb, company.postcode].filter(Boolean).join(", ") || "—"}</dd>
                            </dl>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-head"><h2>Agreement &amp; activity</h2></div>
                        <div className="card-body">
                            <dl className="kv">
                                <dt>Agreement period</dt>
                                <dd>
                                    {company.agreement_start_date
                                        ? `${formatDate(company.agreement_start_date)} – ${company.agreement_end_date ? formatDate(company.agreement_end_date) : "ongoing"}`
                                        : "—"}
                                </dd>
                                <dt>Referrals from them</dt><dd>{mine.referrals.length}</dd>
                                <dt>Open cases</dt><dd>{openCases} of {mine.cases.length}</dd>
                                <dt>Pending requests</dt><dd>{pendingRequests}</dd>
                            </dl>
                            {company.notes && (
                                <p style={{ fontSize: "0.85rem", color: "var(--ink-soft)", whiteSpace: "pre-wrap", marginTop: "0.6rem" }}>
                                    {company.notes}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {tab === "contacts" && (
                <>
                    <div className="card">
                        <div className="card-head"><h2>People</h2></div>
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead><tr><th>Name</th><th>Role</th><th>Email</th><th>Phone</th></tr></thead>
                                    <tbody>
                                        {mine.contacts.map((c) => (
                                            <tr key={c.id}>
                                                <td style={{ fontWeight: 600 }}>
                                                    <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                        <InitialsAvatar name={`${c.first_name} ${c.last_name}`} size={24} />
                                                        {c.first_name} {c.last_name}
                                                    </span>
                                                </td>
                                                <td>{c.job_title ?? "—"}</td>
                                                <td>{c.email ?? "—"}</td>
                                                <td>{c.phone ?? "—"}</td>
                                            </tr>
                                        ))}
                                        {mine.contacts.length === 0 && (
                                            <tr><td colSpan={4} className="empty">No contacts recorded — add them under Providers &amp; contacts.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <ProviderOrgChart companyId={company.id} />
                </>
            )}

            {tab === "participants" && (
                <div className="card">
                    <div className="card-head"><h2>Their participants</h2></div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Participant</th><th>NDIS</th><th>Status</th><th /></tr></thead>
                                <tbody>
                                    {mine.participants.map((p) => (
                                        <tr key={p.id}>
                                            <td style={{ fontWeight: 600 }}>
                                                <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <InitialsAvatar name={p.full_name} size={24} />
                                                    {p.full_name}
                                                </span>
                                            </td>
                                            <td className="mono">{p.ndis_number ?? "—"}</td>
                                            <td><span className={`chip ${p.status === "active" ? "pass" : "mute"}`}>{titleCase(p.status)}</span></td>
                                            <td><Link className="icon-btn" to={`/participants?id=${p.id}`}>Open record ↗</Link></td>
                                        </tr>
                                    ))}
                                    {mine.participants.length === 0 && (
                                        <tr><td colSpan={4} className="empty">No participants linked to this organisation yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "roster" && (
                <div className="card">
                    <div className="card-head">
                        <h2>Shifts for their participants</h2>
                        <Link className="icon-btn" to="/shifts">Open Roster ↗</Link>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>When</th><th>Participant</th><th>Worker</th><th>Status</th></tr></thead>
                                <tbody>
                                    {mine.roster.slice(0, 50).map((s) => (
                                        <tr key={s.id}>
                                            <td className="mono">{formatDate(s.starts_at)}</td>
                                            <td style={{ fontWeight: 600 }}>{participantName(s.participant_id)}</td>
                                            <td>{s.employee_name ?? "Unassigned"}</td>
                                            <td><span className={`chip ${s.status === "completed" ? "pass" : s.status === "confirmed" ? "info" : "mute"}`}>{titleCase(s.status)}</span></td>
                                        </tr>
                                    ))}
                                    {mine.roster.length === 0 && (
                                        <tr><td colSpan={4} className="empty">No shifts rostered for this provider's participants yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "medications" && (
                <div className="card">
                    <div className="card-head"><h2>Medications across their participants</h2></div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Participant</th><th>Medication</th><th>Dose</th><th>Frequency</th><th>Status</th><th /></tr></thead>
                                <tbody>
                                    {mine.medications.map((m) => (
                                        <tr key={m.id}>
                                            <td style={{ fontWeight: 600 }}>{participantName(m.participant_id)}</td>
                                            <td>
                                                {m.name}
                                                {m.is_prn && <span className="chip warn" style={{ marginLeft: 6 }}>PRN</span>}
                                            </td>
                                            <td>{m.dose} · {m.route}</td>
                                            <td>{m.frequency}</td>
                                            <td><span className={`chip ${m.status === "active" ? "pass" : m.status === "ceased" ? "mute" : "warn"}`}>{titleCase(m.status)}</span></td>
                                            <td><Link className="icon-btn" to={`/participants?id=${m.participant_id}`}>Open record ↗</Link></td>
                                        </tr>
                                    ))}
                                    {mine.medications.length === 0 && (
                                        <tr><td colSpan={6} className="empty">No medications recorded for this provider's participants.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "notes" && (
                <div className="card">
                    <div className="card-head"><h2>Service notes across their participants</h2></div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Date</th><th>Participant</th><th>Type</th><th>Note</th><th /></tr></thead>
                                <tbody>
                                    {mine.notes.slice(0, 50).map((n) => (
                                        <tr key={n.id}>
                                            <td className="mono">{formatDate(n.note_date)}</td>
                                            <td style={{ fontWeight: 600 }}>{participantName(n.participant_id)}</td>
                                            <td><span className="chip info">{titleCase(n.kind)}</span></td>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{n.subject}</div>
                                                <div style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>
                                                    {n.body.length > 140 ? `${n.body.slice(0, 140)}…` : n.body}
                                                </div>
                                            </td>
                                            <td><Link className="icon-btn" to={`/participants?id=${n.participant_id}`}>Open record ↗</Link></td>
                                        </tr>
                                    ))}
                                    {mine.notes.length === 0 && (
                                        <tr><td colSpan={5} className="empty">No service notes for this provider's participants yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "handovers" && (
                <div className="card">
                    <div className="card-head"><h2>Handovers across their participants</h2></div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>When</th><th>Participant</th><th>Summary</th><th>Status</th><th /></tr></thead>
                                <tbody>
                                    {mine.handovers.slice(0, 50).map((h) => (
                                        <tr key={h.id}>
                                            <td className="mono">{formatDate(h.occurred_at)}</td>
                                            <td style={{ fontWeight: 600 }}>{participantName(h.participant_id)}</td>
                                            <td style={{ color: "var(--ink-soft)" }}>
                                                {h.summary.length > 120 ? `${h.summary.slice(0, 120)}…` : h.summary}
                                            </td>
                                            <td><span className={`chip ${h.status === "acknowledged" ? "pass" : h.status === "issued" ? "info" : "mute"}`}>{titleCase(h.status)}</span></td>
                                            <td><Link className="icon-btn" to={`/participants?id=${h.participant_id}`}>Open record ↗</Link></td>
                                        </tr>
                                    ))}
                                    {mine.handovers.length === 0 && (
                                        <tr><td colSpan={5} className="empty">No handovers for this provider's participants yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "risk" && (
                <div className="card">
                    <div className="card-head"><h2>Risk assessments</h2></div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Participant</th><th>File</th><th>Note</th><th>Added</th></tr></thead>
                                <tbody>
                                    {mine.riskDocs.map((d) => (
                                        <tr key={d.id}>
                                            <td style={{ fontWeight: 600 }}>{participantName(d.participant_id)}</td>
                                            <td><a href={fileViewUrl(d.file_id)} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>{d.file_name}</a></td>
                                            <td style={{ color: "var(--ink-soft)" }}>{d.note ?? "—"}</td>
                                            <td className="mono">{formatDate(d.created_at)}</td>
                                        </tr>
                                    ))}
                                    {mine.riskDocs.length === 0 && (
                                        <tr><td colSpan={4} className="empty">No risk assessments on file — add them from the participant's record.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "cases" && (
                <div className="card">
                    <div className="card-head">
                        <h2>Episodes of care</h2>
                        <Link className="icon-btn" to="/cases">Open Cases ↗</Link>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Case</th><th>Opened</th><th>Status</th></tr></thead>
                                <tbody>
                                    {mine.cases.map((c) => (
                                        <tr key={c.id}>
                                            <td className="mono" style={{ fontWeight: 700 }}>{c.ref_code}</td>
                                            <td className="mono">{formatDate(c.opened_on)}</td>
                                            <td><span className={`chip ${c.status === "open" ? "pass" : c.status === "on_hold" ? "warn" : "mute"}`}>{titleCase(c.status)}</span></td>
                                        </tr>
                                    ))}
                                    {mine.cases.length === 0 && <tr><td colSpan={3} className="empty">No cases for this provider yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "requests" && (
                <div className="card">
                    <div className="card-head">
                        <h2>Their requests</h2>
                        <Link className="icon-btn" to="/crm/partner-requests">Open queue ↗</Link>
                    </div>
                    <div className="card-body" style={{ display: "grid", gap: "0.4rem" }}>
                        {mine.requests.map((r) => (
                            <div key={r.id} className="file-row">
                                {r.ref_code && <span className="chip mute mono" style={{ fontWeight: 700 }}>{r.ref_code}</span>}
                                <span style={{ fontWeight: 600 }}>{r.subject}</span>
                                <span className="chip info">{titleCase(r.kind)}</span>
                                <span className={`chip ${r.status === "actioned" ? "pass" : r.status === "declined" ? "block" : "warn"}`}>
                                    {titleCase(r.status)}
                                </span>
                            </div>
                        ))}
                        {mine.requests.length === 0 && <div className="empty">No requests from this organisation.</div>}
                    </div>
                </div>
            )}

            {tab === "invoices" && (
                <div className="card">
                    <div className="card-head">
                        <h2>Invoices</h2>
                        <Link className="icon-btn" to={`/crm/invoices?company=${company.id}`}>Open in Invoices ↗</Link>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Invoice</th><th>Period</th><th>Total</th><th>Status</th></tr></thead>
                                <tbody>
                                    {mine.invoices.map((i) => (
                                        <tr key={i.id}>
                                            <td className="mono" style={{ fontWeight: 700 }}>{i.invoice_number}</td>
                                            <td className="mono">{formatDate(i.period_start)} – {formatDate(i.period_end)}</td>
                                            <td className="mono">${Number(i.total).toLocaleString("en-AU")}</td>
                                            <td><span className={`chip ${i.status === "paid" ? "pass" : i.status === "draft" ? "warn" : "info"}`}>{titleCase(i.status)}</span></td>
                                        </tr>
                                    ))}
                                    {mine.invoices.length === 0 && <tr><td colSpan={4} className="empty">Nothing invoiced yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "rates" && (
                <>
                    <p style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                        Negotiated bill rates for this provider — pick <b>{company.name}</b> in the selector below.
                    </p>
                    <NegotiatedRatesCard />
                </>
            )}

            {tab === "documents" && (
                <div className="card">
                    <div className="card-head"><h2>Documents</h2></div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Kind</th><th>Participant</th><th>File</th><th>Note</th><th>Added</th></tr></thead>
                                <tbody>
                                    {mine.docs.map((d) => (
                                        <tr key={d.id}>
                                            <td>
                                                <span className="chip info">{titleCase(d.kind)}</span>
                                                {d.category && <div style={{ fontSize: "0.7rem", color: "var(--muted)" }}>{d.category}</div>}
                                            </td>
                                            <td>{participantName(d.participant_id)}</td>
                                            <td><a href={fileViewUrl(d.file_id)} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>{d.file_name}</a></td>
                                            <td style={{ color: "var(--ink-soft)" }}>{d.note ?? "—"}</td>
                                            <td className="mono">{formatDate(d.created_at)}</td>
                                        </tr>
                                    ))}
                                    {mine.docs.length === 0 && <tr><td colSpan={5} className="empty">No documents from this organisation yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {tab === "settings" && (
                <div className="card">
                    <div className="card-head"><h2>Organisation settings</h2></div>
                    <div className="card-body">
                        <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: "0.6rem" }}>
                            Everything editable about {company.name} — identity, NDIS registration, billing
                            and the agreement window. Portal accounts for their people are invited from
                            Settings → Add users (role Partner, this organisation).
                        </p>
                        <button className="btn" onClick={() => setEditing(true)}>Edit organisation</button>
                    </div>
                </div>
            )}
        </div>
    );
};
